/**
 * AttendanceGeofences widget — org's geofence pins on a map.
 *
 * Migrated from a single hardcoded pin (QuTime HQ, SF) to live data
 * off `/v1/attendance/geofences`. Renders every geofence row as a
 * circle + advanced marker on Google Maps. Auto-fits the viewport to
 * the union of all pins so multi-branch orgs (e.g. Alpha Logistics
 * with HQ Shanghai + regional depots) are visible without panning.
 *
 * Falls back gracefully on the empty-state (no geofences yet) and
 * on the unlikely case that Google Maps fails to load — the widget
 * degrades to a small info panel instead of crashing the dashboard.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Circle,
  GoogleMap,
} from '@react-google-maps/api';
import { InfoOutlined, PlaceOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { ApiError, attendanceApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import {
  GoogleMapsErrorBoundary,
  useSvdyGoogleMaps,
} from '../../../../components/location/googleMaps';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';

interface Geofence {
  id: string;
  name: string;
  lat: number | string;
  lng: number | string;
  radius_meters: number;
  address: string | null;
  enabled: boolean;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: 2,
};

const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '';
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  ...(mapId
    ? { mapId }
    : {
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      }),
};

const circleOptions = {
  strokeColor: '#2E65D4',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: '#2E65D4',
  fillOpacity: 0.15,
  clickable: false,
  draggable: false,
  editable: false,
  visible: true,
  zIndex: 1,
};

/**
 * Decimal coordinates arrive from the backend as strings (Numeric
 * column). Parse with graceful fallback.
 */
function toFloat(v: number | string): number {
  return typeof v === 'number' ? v : parseFloat(v);
}

export const AttendanceGeofencesMap: React.FC<{ geofences: Geofence[] }> = ({ geofences }) => {
  const { t } = useTranslation();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<any[]>([]);

  const { isLoaded, loadError } = useSvdyGoogleMaps();

  // Center + zoom derived from the geofence cloud. Single pin → zoom 17;
  // multi-location → fitBounds after the map loads (see effect below).
  const points = useMemo(
    () =>
      geofences.map((g) => ({
        lat: toFloat(g.lat),
        lng: toFloat(g.lng),
        name: g.name,
      })),
    [geofences],
  );

  const initialCenter =
    points.length > 0 ? { lat: points[0].lat, lng: points[0].lng } : { lat: 0, lng: 0 };
  const initialZoom = points.length === 1 ? 17 : 10;

  const onLoad = useCallback((instance: google.maps.Map) => {
    setMap(instance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // fitBounds to include every pin when the map loads or the geofence
  // list changes. Single-pin orgs skip this and keep the initial zoom.
  useEffect(() => {
    if (!map || points.length < 2 || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    for (const p of points) bounds.extend({ lat: p.lat, lng: p.lng });
    map.fitBounds(bounds, 60);
  }, [map, points]);

  // Create / refresh advanced markers imperatively. JSX markers via
  // @react-google-maps/api can race with MUI Dialog transitions on
  // some surfaces (saw this bite LocationPickerDialog earlier).
  useEffect(() => {
    if (!isLoaded || !map || !window.google?.maps) return;
    // Clear previous markers
    for (const m of markersRef.current) {
      m.map = null;
    }
    markersRef.current = [];
    try {
      const markerNs = window.google.maps.marker;
      for (const p of points) {
        // ADVANCED MARKERS REQUIRE A MAP ID, and this branch used to ignore that (owner console,
        // 2026-09-08: "The map is initialized without a valid Map ID, which will prevent use of Advanced
        // Markers").
        //
        // `mapOptions` above sets `mapId` only when `REACT_APP_GOOGLE_MAPS_MAP_ID` is configured, and
        // falls back to inline `styles` otherwise — the two are mutually exclusive in the Maps API. But
        // the test here was `markerNs.AdvancedMarkerElement`, i.e. "is the marker library loaded", which
        // is true either way. So on a deployment with no Map ID the code asked for a marker class the map
        // cannot host: Google logs the warning and the pins do not render.
        //
        // The condition now matches what `mapOptions` actually did. `Marker` is deprecated but is the
        // correct class for a styles-based map, and it is the only one that works without a Map ID.
        if (mapId && markerNs && markerNs.AdvancedMarkerElement) {
          const m = new markerNs.AdvancedMarkerElement({
            position: { lat: p.lat, lng: p.lng },
            title: p.name,
            map,
          });
          markersRef.current.push(m);
        } else {
          const m = new window.google.maps.Marker({
            position: { lat: p.lat, lng: p.lng },
            title: p.name,
            map,
          });
          markersRef.current.push(m);
        }
      }
    } catch (err) {
      // Markers are nice-to-have; Circle always renders. Don't crash
      // the widget if the marker API misbehaves.
      // eslint-disable-next-line no-console
      console.warn('Geofence marker creation failed', err);
    }
    return () => {
      for (const m of markersRef.current) {
        m.map = null;
      }
      markersRef.current = [];
    };
  }, [isLoaded, map, points]);

  if (loadError) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="error">{t('dashboard:widgets.attendanceGeofences.errorLoadingMaps')}</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={initialCenter}
      zoom={initialZoom}
      options={mapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {points.map((p, idx) => (
        <Circle
          key={`${p.lat}-${p.lng}-${idx}`}
          center={{ lat: p.lat, lng: p.lng }}
          radius={geofences[idx]?.radius_meters ?? 200}
          options={circleOptions}
        />
      ))}
    </GoogleMap>
  );
};

const AttendanceGeofences: React.FC = () => {
  const { t } = useTranslation();
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = (await attendanceApi.geofencesApi.list()) as Geofence[];
        if (cancelled) return;
        setGeofences(raw.filter((g) => g.enabled));
      } catch (e) {
        if (!cancelled) {
          const msg =
            e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.attendanceGeofences.loadError');
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (geofences.length === 0) {
    return (
      <WidgetEmptyState
        icon={PlaceOutlined}
        title={t('dashboard:widgets.attendanceGeofences.noGeofences')}
        hint={t('dashboard:widgets.attendanceGeofences.noGeofencesHint')}
      />
    );
  }

  // Header: show the primary geofence (first row — typically HQ) +
  // an info tooltip summarising how many pins total.
  const headline = geofences[0];
  const otherCount = geofences.length - 1;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Not a Paper. Dashboard.tsx already gives every widget a bordered Paper
          with p: 2, so a nested one on the same background put this card's summary
          row at 16 + 16 = 32px and stacked a second (invisible but real) card
          edge — svdy-ui §15's "no card inside a card". A Box keeps the layout and
          drops the second inset. */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* A real anchor, not an `onClick` Box. This is an inline text link, so
              it has to be reachable by keyboard and openable in a new tab — and
              before 2026-08-05 it was neither: it carried `cursor: 'pointer'`, a
              hover underline and `primary.main`, and NO handler of any kind. The
              widget file contained zero `navigate(` and zero `onClick`, so the
              click the styling promised had never existed. Geofences are edited on
              Organization → Branches & Locations, which is where the map's own
              data comes from. */}
          <Typography
            component={RouterLink}
            to="/organization/branches-locations"
            variant="subtitle2"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'none',
              mb: 0.5,
              '&:hover': { textDecoration: 'underline' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={headline.name}
          >
            {headline.name}
            {otherCount > 0
              ? t('dashboard:widgets.attendanceGeofences.moreCount', { count: otherCount })
              : ''}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={headline.address ?? ''}
          >
            {headline.address ?? '—'}
          </Typography>
        </Box>
        <Tooltip
          title={t('dashboard:widgets.attendanceGeofences.summary', {
            count: geofences.length,
            radius: Math.round(
              geofences.reduce((sum, geofence) => sum + geofence.radius_meters, 0)
                / geofences.length,
            ),
          })}
        >
          <IconButton
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'primary.lighter',
              },
            }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <GoogleMapsErrorBoundary
          fallback={(
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="error">
                {t('dashboard:widgets.attendanceGeofences.errorLoadingMaps')}
              </Typography>
            </Box>
          )}
        >
          <AttendanceGeofencesMap geofences={geofences} />
        </GoogleMapsErrorBoundary>
      </Box>
    </Box>
  );
};

export default AttendanceGeofences;
